import React, { useState } from 'react'
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Tooltip,
} from '@mui/material'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'

// Define the props interface
interface CopyInputFieldProps {
  value: string // Expecting the 'value' prop to be a string
  label?: string // Optional label for the input field
}

// Function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
    } else {
      // Use the 'out of viewport hidden text area' trick
      const textArea = document.createElement('textarea')
      textArea.value = text

      // Move textarea out of the viewport so it's not visible
      textArea.style.position = 'absolute'
      textArea.style.left = '-999999px'

      document.body.prepend(textArea)
      textArea.select()

      try {
        document.execCommand('copy')
      } catch (error) {
        console.error('Error copying text:', error)
      } finally {
        textArea.remove()
      }
    }
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

// The main component
const CopyInputField: React.FC<CopyInputFieldProps> = ({ value, label = 'Copy Field' }) => {
  const [tooltipMessage, setTooltipMessage] = useState<string>('Click to copy')

  const handleCopyClick = async () => {
    await copyToClipboard(value)
    setTooltipMessage('Copied!')
    setTimeout(() => setTooltipMessage('Click to copy'), 2000)
  }

  return (
    <FormControl variant='outlined' fullWidth>
      <InputLabel htmlFor='copy-input-field'>{label}</InputLabel>
      <OutlinedInput
        id='copy-input-field'
        type='text'
        value={value}
        defaultValue={value}
        readOnly // Make the input read-only to prevent editing
        endAdornment={
          <InputAdornment position='end'>
            <Tooltip title={tooltipMessage} arrow>
              <IconButton edge='end' onClick={handleCopyClick}>
                <ContentCopyRoundedIcon />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        }
        label={label}
      />
    </FormControl>
  )
}

export default CopyInputField
